import { useState } from "react";
import { ApiError } from "@common/api";
import { formatSize } from "@common/format";
import { downloadCloudRom, type CloudRom } from "../../library/cloudLibrary";
import { errorText, useCloudRoms } from "../../library/queries";
import { isNative } from "../../desktop/desktop";
import { displayName } from "../saves";
import type { Emulator } from "../useEmulator";
import { Icon } from "@jkast90/kast-common/ui";

// Picks a game from the cloud library into the running player. Only
// offered signed in; the API client refuses without a token anyway.
export function CloudLibrary({ emu, onClose }: { emu: Emulator; onClose: () => void }) {
  const library = useCloudRoms();
  const roms = library.data ?? null;
  const [downloadError, setError] = useState<string | null>(null);
  const error = downloadError ?? (library.error ? errorText(library.error, "Could not load the cloud library.") : null);
  const [downloading, setDownloading] = useState<{ id: string; fraction: number } | null>(null);

  const download = async (rom: CloudRom) => {
    if (downloading) return;
    setDownloading({ id: rom.id, fraction: 0 });
    setError(null);
    try {
      const file = await downloadCloudRom(rom, (fraction) => setDownloading({ id: rom.id, fraction }));
      await emu.addRom(file);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Download failed.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cloud Library</h2>
          <button className="btn icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" size={20} className="icon" />
          </button>
        </div>

        <div className="modal-body">
          {roms === null && !error && <p className="muted">Loading ROMs from cloud…</p>}
          {error && <p className="notice error">{error}</p>}
          {roms?.length === 0 && <p className="muted">No ROMs in the cloud library yet. Add a game while signed in to share it.</p>}
          {roms && roms.length > 0 && (
            <ul className="rom-list">
              {roms.map((rom) => (
                <li key={rom.id}>
                  <div>
                    <span className="name" title={rom.name}>
                      {displayName(rom.name)}
                    </span>
                    <span className="muted small">
                      {rom.system.toUpperCase()} · {formatSize(rom.size)}
                    </span>
                  </div>
                  {/* Disc games are listed but play in the desktop app, not this player. */}
                  {isNative(rom.system) ? (
                    <span className="muted small">Desktop app</span>
                  ) : (
                    <button type="button" className="btn" disabled={downloading !== null} onClick={() => download(rom)}>
                      {downloading?.id === rom.id ? `${Math.round(downloading.fraction * 100)}%` : "Download"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
